<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: "Do my accounts hold what the books say, as of this date?"
  Answer:   the difference column and the "what finishing will post" panel.
  Context:  account name, currency and type, the computed balance, the date, the progress
            count, and the save status.
  Controls: the actual inputs, the transfer switch, Finish and Discard.
-->
<script setup lang="ts">
import {
  formatInstantDate,
  formatLocalDate,
  formatMoney,
  type Money,
  parseLocalDate,
  todayUtc,
  systemClock,
} from '@taler/domain'
import { ActionButton, Card, ErrorState, PageHeader, PageShell, StatusTag } from '@taler/ui'
import DetailNotFound from '@/app/components/DetailNotFound.vue'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useSession } from '@/app/session'
import {
  canReconcile,
  canViewReconciliations,
  ReconciliationProposal,
  ReconciliationTable,
  useAccountLookup,
  useReconciliation,
} from '@/features/reconciliation'
import type { ReconciliationRow } from '@/features/reconciliation'

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')
const session = useSession()
const route = useRoute()
const router = useRouter()

const id = computed(() => String(route.params['id'] ?? ''))
const ledgerId = computed(() => session.activeLedger.value?.id)
const canView = computed(() => canViewReconciliations(session.can))
const canManage = computed(() => canReconcile(session.can))

const reconciliation = useReconciliation({
  ledgerId: () => ledgerId.value,
  id: () => id.value,
  enabled: () => canView.value,
})

const accounts = useAccountLookup({
  ledgerId: () => ledgerId.value,
  locale: () => currentLocale.value,
  enabled: () => canView.value,
})

const applyReallocations = ref(true)
const isOpen = computed(() => reconciliation.detail.value?.status === 'OPEN')

const enteredCount = computed(
  () => reconciliation.detail.value?.rows.filter((row) => row.observedBalance !== undefined).length ?? 0,
)
const totalCount = computed(() => reconciliation.detail.value?.rows.length ?? 0)
const matchedCount = computed(() => reconciliation.detail.value?.proposal?.matchedAccountIds.length ?? 0)
const notCheckedCount = computed(() => totalCount.value - enteredCount.value)

const canFinish = computed(
  () =>
    isOpen.value &&
    enteredCount.value > 0 &&
    !reconciliation.isFinishing.value &&
    reconciliation.savingAccountIds.value.size === 0,
)

async function saveLine(accountId: string, observedBalance: Money): Promise<void> {
  await reconciliation.saveLine(accountId, observedBalance)
}

async function clearLine(accountId: string): Promise<void> {
  await reconciliation.clearLine(accountId)
}

async function finish(): Promise<void> {
  const outcome = await reconciliation.finish(applyReallocations.value)
  if (!outcome.ok) return
}

const confirmingDiscard = ref(false)

async function abandon(): Promise<void> {
  await reconciliation.abandon()
  await router.push({ name: 'reconciliations' })
}

async function changeDate(event: Event): Promise<void> {
  const value = (event.target as HTMLInputElement).value
  if (value === '') return
  await reconciliation.updateDate(parseLocalDate(value))
}

const todayText = computed(() => formatLocalDate(todayUtc(systemClock)))

function moneyText(value: Money): string {
  return formatMoney(value, { locale: currentLocale.value })
}

function outcomeFor(row: ReconciliationRow, allRows: readonly ReconciliationRow[]): string | undefined {
  switch (row.outcome) {
    case 'MATCHED':
      return t('reconciliation.detail.outcomeMatched')
    case 'CORRECTED':
      return t('reconciliation.detail.outcomeCorrected')
    case 'REVERSED':
      return t('reconciliation.detail.outcomeReversed')
    case 'TRANSFERRED': {
      const partner = allRows.find(
        (candidate) =>
          candidate.accountId !== row.accountId && candidate.correctingEntryId === row.correctingEntryId,
      )
      return t('reconciliation.detail.outcomeTransferred', {
        name: accounts.value.get(partner?.accountId ?? '')?.label,
      })
    }
    case undefined:
      return undefined
  }
}
</script>

<template>
  <PageShell class="gap-[var(--space-section)]">
    <PageHeader :title="t('reconciliation.editor.title')">
      <template #actions>
        <p
          v-if="isOpen && (reconciliation.hasSaved.value || reconciliation.savingAccountIds.value.size > 0)"
          role="status"
          class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]"
        >
          {{
            reconciliation.savingAccountIds.value.size > 0
              ? t('reconciliation.editor.saving')
              : t('reconciliation.editor.saved')
          }}
        </p>
      </template>
    </PageHeader>

    <p v-if="session.isLoading.value || reconciliation.isLoading.value" role="status">
      {{ t('app.loading') }}
    </p>
    <p v-else-if="!canView" class="text-[color:var(--color-text-muted)]">
      {{ t('reconciliation.list.noAccess') }}
    </p>
    <ErrorState
      v-else-if="reconciliation.error.value && !reconciliation.notFound.value"
      :title="t('reconciliation.editor.errorTitle')"
      :body="t('reconciliation.list.errorBody')"
      :retry-label="t('reconciliation.list.retry')"
      :retrying="reconciliation.retrying.value"
      @retry="reconciliation.retry()"
    />
    <DetailNotFound
      v-else-if="reconciliation.detail.value === undefined"
      :to="{ name: 'reconciliations' }"
      :list="t('reconciliation.list.title')"
    />

    <template v-else>
      <div class="flex flex-wrap items-center gap-[var(--space-group)]">
        <label class="flex items-center gap-[var(--space-inline)]">
          {{ t('reconciliation.editor.dateLabel') }}
          <input
            type="date"
            :value="formatLocalDate(reconciliation.detail.value.observedDate)"
            :max="todayText"
            :disabled="!isOpen || !canManage"
            class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-[var(--space-cell)] py-[var(--space-inline)]"
            @change="changeDate"
          />
        </label>
        <span class="text-[color:var(--color-text-muted)]">
          {{ t('reconciliation.editor.progress', { entered: enteredCount, total: totalCount }) }}
        </span>
      </div>

      <ReconciliationTable
        :rows="reconciliation.detail.value.rows"
        :accounts="accounts"
        :saving-account-ids="reconciliation.savingAccountIds.value"
        :changed-account-ids="reconciliation.changedAccountIds.value"
        :line-error-details="reconciliation.lineErrorDetails.value"
        :disabled="!isOpen || !canManage || reconciliation.isFinishing.value"
        :locale="currentLocale"
        @save="saveLine"
        @clear="clearLine"
      />

      <template v-if="isOpen && reconciliation.detail.value.proposal !== undefined">
        <ReconciliationProposal
          :proposal="reconciliation.detail.value.proposal"
          :accounts="accounts"
          :apply-reallocations="applyReallocations"
          :matched-count="matchedCount"
          :not-checked-count="notCheckedCount"
          :locale="currentLocale"
          @update:apply-reallocations="applyReallocations = $event"
        />

        <StatusTag v-if="reconciliation.finishErrorDetail.value" tone="danger">
          {{ reconciliation.finishErrorDetail.value }}
        </StatusTag>
        <p v-else-if="reconciliation.finishErrorPending.value" class="text-[color:var(--color-text-muted)]">
          {{ t('reconciliation.editor.retryFinish') }}
        </p>

        <div v-if="canManage" class="flex justify-end gap-[var(--space-inline)]">
          <ActionButton variant="secondary" type="button" @click="confirmingDiscard = true">
            {{ t('reconciliation.editor.discard') }}
          </ActionButton>
          <ActionButton type="button" size="lg" :disabled="!canFinish" @click="finish">
            {{
              reconciliation.isFinishing.value
                ? t('reconciliation.editor.finishing')
                : t('reconciliation.editor.finish')
            }}
          </ActionButton>
        </div>

        <Card
          v-if="confirmingDiscard"
          padding="block"
          class="flex items-center justify-between gap-[var(--space-cell)]"
        >
          <p>{{ t('reconciliation.editor.discardConfirm', { count: enteredCount }) }}</p>
          <div class="flex gap-[var(--space-inline)]">
            <button type="button" class="underline" @click="confirmingDiscard = false">
              {{ t('reconciliation.editor.cancel') }}
            </button>
            <ActionButton type="button" :disabled="reconciliation.isAbandoning.value" @click="abandon">
              {{ t('reconciliation.editor.discardConfirmAction') }}
            </ActionButton>
          </div>
        </Card>
      </template>

      <template v-else>
        <p class="text-[color:var(--color-text-muted)]">
          {{
            t('reconciliation.detail.finishedBy', {
              who: reconciliation.detail.value.finishedBy,
              when: reconciliation.detail.value.finishedAt
                ? formatInstantDate(reconciliation.detail.value.finishedAt, currentLocale)
                : '',
            })
          }}
        </p>
        <table class="w-full border-collapse text-[length:var(--text-step-2)]">
          <thead>
            <tr class="table-head">
              <th class="px-[var(--space-cell)] py-[var(--space-inline)]">
                {{ t('reconciliation.detail.account') }}
              </th>
              <th class="px-[var(--space-cell)] py-[var(--space-inline)]">
                {{ t('reconciliation.detail.computedThen') }}
              </th>
              <th class="px-[var(--space-cell)] py-[var(--space-inline)]">
                {{ t('reconciliation.detail.actual') }}
              </th>
              <th class="px-[var(--space-cell)] py-[var(--space-inline)]">
                {{ t('reconciliation.detail.outcome') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in reconciliation.detail.value.rows"
              :key="row.accountId"
              class="border-b border-[color:var(--color-border)]"
            >
              <td class="px-[var(--space-cell)] py-[var(--space-inline)]">
                {{ accounts.get(row.accountId)?.label }}
              </td>
              <td class="px-[var(--space-cell)] py-[var(--space-inline)] tabular-nums">
                {{ moneyText(row.computedBalance) }}
              </td>
              <td class="px-[var(--space-cell)] py-[var(--space-inline)] tabular-nums">
                {{ row.observedBalance ? moneyText(row.observedBalance) : '' }}
              </td>
              <td class="px-[var(--space-cell)] py-[var(--space-inline)]">
                <span>{{ outcomeFor(row, reconciliation.detail.value.rows) }}</span>
                <RouterLink
                  v-if="row.correctingEntryId !== undefined"
                  :to="{ name: 'transactions', query: { entry: row.correctingEntryId } }"
                  class="ml-2 text-[color:var(--color-accent)] underline"
                >
                  {{ t('reconciliation.detail.viewEntry') }}
                </RouterLink>
              </td>
            </tr>
          </tbody>
        </table>
      </template>
    </template>
  </PageShell>
</template>
